import { useLocation } from 'react-router-dom';
import { Keyboard } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useShortcutsPanelStore, useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';

interface Shortcut {
  keys: string[];
  description: string;
}

const NAVIGATION: Shortcut[] = [
  { keys: ['Ctrl', 'K'], description: 'Abrir paleta de comandos' },
  { keys: ['⌘', 'K'], description: 'Abrir paleta de comandos (macOS)' },
  { keys: ['?'], description: 'Mostrar este panel de atajos' },
  { keys: ['Esc'], description: 'Cerrar diálogos / paneles abiertos' },
];

const SIDEBAR: Shortcut[] = [
  { keys: ['['], description: 'Colapsar sidebar (solo iconos)' },
  { keys: [']'], description: 'Expandir sidebar' },
];

// Contextual shortcuts per route prefix
const PAGE_SHORTCUTS: Array<{ match: (path: string) => boolean; title: string; items: Shortcut[] }> = [
  {
    match: (p) => p.startsWith('/production/wip') || p.startsWith('/production/dispatch'),
    title: 'Producción',
    items: [
      { keys: ['G', 'O'], description: 'Ir a Órdenes de Trabajo (WIP)' },
      { keys: ['N'], description: 'Nueva orden (cuando esté disponible)' },
    ],
  },
  {
    match: (p) => p.startsWith('/quality'),
    title: 'Calidad',
    items: [
      { keys: ['N'], description: 'Nueva No-Conformidad' },
      { keys: ['F'], description: 'Filtrar NCs críticas' },
    ],
  },
  {
    match: (p) => p.startsWith('/traceability'),
    title: 'Trazabilidad',
    items: [
      { keys: ['/'], description: 'Enfocar buscador' },
    ],
  },
  {
    match: (p) => p.startsWith('/backoffice/devices'),
    title: 'Dispositivos',
    items: [
      { keys: ['R'], description: 'Refrescar estado de dispositivos' },
    ],
  },
];

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded border border-border bg-muted font-mono text-[11px] text-foreground shadow-sm">
      {children}
    </kbd>
  );
}

function ShortcutRow({ s }: { s: Shortcut }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <span className="text-sm text-muted-foreground">{s.description}</span>
      <div className="flex items-center gap-1">
        {s.keys.map((k, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <span className="text-[10px] text-muted-foreground">+</span>}
            <Kbd>{k}</Kbd>
          </span>
        ))}
      </div>
    </div>
  );
}

function Section({ title, items }: { title: string; items: Shortcut[] }) {
  return (
    <div className="space-y-1">
      <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
      <div className="rounded-md border border-border bg-card/40 px-3 py-1.5 divide-y divide-border/60">
        {items.map((s, i) => <ShortcutRow key={i} s={s} />)}
      </div>
    </div>
  );
}

/**
 * Keyboard-shortcuts help dialog.
 *
 * Mounts the global "?" / "[" / "]" listeners via {@link useKeyboardShortcuts}
 * and renders a categorized cheatsheet (Navigation, Sidebar, Page-contextual).
 */
export function KeyboardShortcutsPanel() {
  useKeyboardShortcuts();
  const open = useShortcutsPanelStore((s) => s.open);
  const setOpen = useShortcutsPanelStore((s) => s.setOpen);
  const { pathname } = useLocation();
  const pageSection = PAGE_SHORTCUTS.find((p) => p.match(pathname));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="h-4 w-4" />
            Atajos de teclado
          </DialogTitle>
          <DialogDescription>
            Pulsa <Kbd>?</Kbd> en cualquier momento para abrir este panel.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <Section title="Navegación" items={NAVIGATION} />
          <Section title="Sidebar" items={SIDEBAR} />
          {pageSection && <Section title={`Página actual · ${pageSection.title}`} items={pageSection.items} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
